"use client";

import { startTransition, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Link } from "@/i18n/navigation";
import { artistLinkRel, eventLinkRel } from "@/lib/seo-enrichment";
import Image, { getImageProps } from "next/image";
import { ChevronLeft, ChevronRight, ExternalLink, MapPin, Calendar, ThumbsUp, ThumbsDown, Plus, Check, X } from "lucide-react";
import { Event } from "@/lib/types";
import { formatDate, formatTime, formatPrice, formatPriceCompact } from "@/lib/utils";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { ArtistExternalLinks } from "../artist-external-links";
import { SubscribeButton } from "../subscribe-button";
import { ReportButton } from "../report/report-button";
import { useAuth } from "../auth/auth-context";
import { ArtistEditModal, ArtistEditInput } from "../admin/artist-edit-modal";
import { useScheduleBuilder } from "../festivals/schedule/schedule-context";
import { EventGenreChip } from "./event-genre-chip";
import { useGenreTagLinkMode } from "./feed-genre-filter-context";
import { PlayIcon } from "@/components/spotify/play-icon";
import { JukeboxShareButton } from "./jukebox-share-button";
import type { JukeboxArtist, JukeboxEvent } from "@/lib/jukebox-playlist";
export type { JukeboxArtist, JukeboxEvent } from "@/lib/jukebox-playlist";

function jukeboxArtistLinkRel(artist: JukeboxArtist) {
  return artistLinkRel({ image_url: artist.imageUrl, spotify_id: artist.spotifyId, genres: artist.genres });
}

type NavDir = "next" | "prev";
type SwipeAxis = "x" | "y";

/**
 * The sleeve's `sizes` per layout. The preloader mirrors these so it warms the
 * exact variant the visible image will request.
 */
const SLEEVE_SIZES = { desktop: "400px", mobile: "100vw" } as const;
/** Matches the 950px layout breakpoint where the sleeve moves into the fixed left column. */
const DESKTOP_SLEEVE_QUERY = "(min-width: 950px)";
/** Records ahead (plus one behind) whose lead-artist sleeve image is warmed. */
const SLEEVE_PRELOAD_AHEAD = 3;

interface SleeveData {
  artist: JukeboxArtist;
  event: Event;
  imageUrl: string | null;
  name: string;
  genres: string[];
}

function submitGenreVote(attractionId: number, genre: string, vote: "up" | "down") {
  fetch("/api/genre-match-vote", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ attractionId, genre, vote }),
  }).catch(() => {});
}

interface GenreJukeboxModalProps {
  playlist: JukeboxEvent[];
  genreName: string;
  selectedGenres: string[];
  citySlug: string;
  initialIndex?: number;
  initialArtistId?: number;
  /** Localized page and filters defining this playlist. */
  playlistHref?: string;
  standalone?: boolean;
  playlistPending?: boolean;
  playlistError?: boolean;
  onRetryPlaylist?: () => void;
  onClose: (lastIndex: number) => void;
  titleOverride?: string;
  /** When set, enables "add to schedule" for festival jukebox */
  festivalId?: number;
}

export function GenreJukeboxModal({
  playlist,
  genreName,
  selectedGenres,
  citySlug,
  initialIndex = 0,
  initialArtistId,
  playlistHref,
  standalone = false,
  playlistPending = false,
  playlistError = false,
  onRetryPlaylist,
  onClose,
  titleOverride,
  festivalId,
}: GenreJukeboxModalProps) {
  const t = useTranslations("Jukebox");
  const tCommon = useTranslations("Common");
  const directoryTags = useGenreTagLinkMode() === "directory";
  const tEvents = useTranslations("Events");
  const locale = useLocale();
  const { play, startPlayback, artistId: activeArtistId, setJukeboxOpen } = useSpotifyPlayer();
  const { isAdmin } = useAuth();

  const safeInitial = Math.min(Math.max(0, initialIndex), Math.max(0, playlist.length - 1));
  const [eventIndex, setEventIndex] = useState(safeInitial);
  const eventIndexRef = useRef(safeInitial);
  const [preferredArtistId, setPreferredArtistId] = useState(initialArtistId);
  const [playlistStarted, setPlaylistStarted] = useState(!standalone);
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { eventIndexRef.current = eventIndex; }, [eventIndex]);
  const handleClose = useCallback(() => onClose(eventIndexRef.current), [onClose]);

  // Admin: artist edit modal + local overrides for image/genres
  const [editingArtist, setEditingArtist] = useState<ArtistEditInput | null>(null);
  const [artistOverrides, setArtistOverrides] = useState<Map<number, { imageUrl: string | null; genres: string[]; name: string }>>(new Map());

  // Genre vote tracking — key is "attractionId:genre"
  const singleGenre = selectedGenres.length === 1 ? selectedGenres[0] : null;
  const genreVotedRef = useRef<Map<string, "up" | "down">>(new Map());
  const [currentGenreVote, setCurrentGenreVote] = useState<"up" | "down" | null>(null);

  const currentEvent = playlist[eventIndex];
  // The "featured" artist is the first one, or whichever is currently playing
  const playingArtistIdx = currentEvent?.artists.findIndex(
    (a) => a.spotifyId === activeArtistId
  );
  const featuredArtist = currentEvent?.artists.find((artist) => artist.attractionId === preferredArtistId) ?? (
    playingArtistIdx >= 0
      ? currentEvent.artists[playingArtistIdx]
      : currentEvent?.artists[0]);

  const playArtist = useCallback(
    (spotifyId: string, name: string, start = false) => {
      // An immediate selection/navigation supersedes delayed launch playback.
      if (autoplayTimerRef.current) clearTimeout(autoplayTimerRef.current);
      setPlaylistStarted(true);
      setPreferredArtistId(undefined);
      const entry = playlist[eventIndexRef.current];
      const artist = entry?.artists.find((artist) => artist.spotifyId === spotifyId);
      const target = entry ? { event: entry.event, artistId: artist?.attractionId } : undefined;
      (start ? startPlayback : play)(spotifyId, name, target);
    },
    [play, startPlayback, playlist]
  );

  const startPlayer = useCallback(() => {
    if (!featuredArtist) return;
    playArtist(featuredArtist.spotifyId, featuredArtist.name, true);
  }, [featuredArtist, playArtist]);

  // A shared artist takes precedence over an already playing act on this bill.
  useEffect(() => {
    // Shared links wait for Start Player, which supplies the audio gesture.
    if (standalone) return;
    const requested = currentEvent?.artists.find((artist) => artist.attractionId === initialArtistId);
    const artist = requested ?? currentEvent?.artists.find((artist) => artist.spotifyId === activeArtistId) ?? currentEvent?.artists[0];
    if (!artist) return;
    if (artist.spotifyId === activeArtistId) {
      // Attach the current bill without rebuilding the already-playing embed.
      startPlayback(artist.spotifyId, artist.name, { event: currentEvent.event, artistId: artist.attractionId });
      return;
    }
    const timer = setTimeout(() => {
      playArtist(artist.spotifyId, artist.name, true);
    }, 150);
    autoplayTimerRef.current = timer;
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync genre vote state when featured artist changes
  useEffect(() => {
    if (!singleGenre || !featuredArtist) {
      setCurrentGenreVote(null);
      return;
    }
    const key = `${featuredArtist.attractionId}:${singleGenre}`;
    setCurrentGenreVote(genreVotedRef.current.get(key) ?? null);
  }, [featuredArtist, singleGenre]);

  // Direction of the last next/prev move. It drives the sleeve slide and the
  // readout slide; `seq` lets the sleeve tell a navigation apart from a
  // featured-artist change on the same bill (which crossfades instead).
  const [nav, setNav] = useState<{ dir: NavDir | null; seq: number; animate: boolean }>({ dir: null, seq: 0, animate: true });

  const goTo = useCallback(
    (idx: number, dir: NavDir, animate = true) => {
      setEventIndex(idx);
      eventIndexRef.current = idx;
      setNav((n) => ({ dir, seq: n.seq + 1, animate }));
      const lead = playlist[idx].artists[0];
      // Playback updates every Spotify consumer, including the event feed
      // behind the modal. Let the record animate before rendering that work.
      startTransition(() => playArtist(lead.spotifyId, lead.name, true));
    },
    [playlist, playArtist]
  );

  const goNext = useCallback(() => {
    if (playlistStarted && eventIndex < playlist.length - 1) goTo(eventIndex + 1, "next");
  }, [playlistStarted, eventIndex, playlist.length, goTo]);

  const goPrev = useCallback(() => {
    if (playlistStarted && eventIndex > 0) goTo(eventIndex - 1, "prev");
  }, [playlistStarted, eventIndex, goTo]);

  const isAtStart = eventIndex === 0;
  const isAtEnd = eventIndex === playlist.length - 1;

  // Warm the sleeve images one record back and a few ahead so a flick lands
  // on a cached face and the wipe never reveals a blank. A detached <img> with
  // the same srcset/sizes fetches the exact candidate the visible image will
  // pick, lands in the browser cache, and avoids the unused-preload warnings a
  // <link rel="preload"> would log for records the listener never reaches.
  const warmedSleevesRef = useRef<Set<string>>(new Set());
  const inFlightSleevesRef = useRef<Set<HTMLImageElement>>(new Set());
  useEffect(() => {
    const sizes = window.matchMedia(DESKTOP_SLEEVE_QUERY).matches ? SLEEVE_SIZES.desktop : SLEEVE_SIZES.mobile;
    for (let offset = -1; offset <= SLEEVE_PRELOAD_AHEAD; offset++) {
      // goTo() plays the lead act, so that's the face the next record opens on.
      const lead = playlist[eventIndex + offset]?.artists[0];
      if (!lead) continue;
      const url = artistOverrides.get(lead.attractionId)?.imageUrl ?? lead.imageUrl;
      if (!url) continue;
      const key = `${sizes}|${url}`;
      if (warmedSleevesRef.current.has(key)) continue;
      warmedSleevesRef.current.add(key);
      // The current record's sleeve is loading in the DOM already; just remember it.
      if (offset === 0) continue;
      const { props } = getImageProps({ src: url, alt: "", fill: true, sizes });
      const img = new window.Image();
      // Keep the element alive until the fetch settles; never outrank the visible sleeve.
      const inFlight = inFlightSleevesRef.current;
      inFlight.add(img);
      img.onload = img.onerror = () => inFlight.delete(img);
      img.setAttribute("fetchpriority", "low");
      if (props.sizes) img.sizes = props.sizes;
      if (props.srcSet) img.srcset = props.srcSet;
      img.src = props.src;
    }
  }, [eventIndex, playlist, artistOverrides]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleClose, goNext, goPrev]);

  // Swipe navigation with live tracking (mobile)
  const touchStartRef = useRef<{ x: number; y: number; time: number; fromNextButton: boolean } | null>(null);
  const swipeAxisRef = useRef<SwipeAxis | "scroll" | null>(null);
  const gestureSurfaceRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mobilePanelRef = useRef<HTMLDivElement>(null);
  const currentPanelRef = useRef<HTMLDivElement>(null);
  const nextPanelRef = useRef<HTMLDivElement>(null);
  const prevPanelRef = useRef<HTMLDivElement>(null);
  const swipeRef = useRef<{ dir: NavDir; axis: SwipeAxis; progress: number; settling: boolean }>({ dir: "next", axis: "x", progress: 0, settling: false });
  const swipeAnimationsRef = useRef<Animation[]>([]);

  const swipeTransform = useCallback((offset: number, axis: SwipeAxis) => (
    axis === "y"
      ? `translate3d(0, ${offset * (scrollRef.current?.clientHeight ?? 0)}px, 0)`
      : `translate3d(${offset * 100}%, 0, 0)`
  ), []);

  // Move each whole record panel without rendering on every touch move.
  const paintSwipe = useCallback((dir: NavDir, progress: number, axis: SwipeAxis = "x") => {
    const offset = dir === "next" ? -progress : progress;
    if (currentPanelRef.current) currentPanelRef.current.style.transform = swipeTransform(offset, axis);
    if (nextPanelRef.current) nextPanelRef.current.style.transform = swipeTransform(1 + offset, axis);
    if (prevPanelRef.current) prevPanelRef.current.style.transform = swipeTransform(-1 + offset, axis);
    swipeRef.current.dir = dir;
    swipeRef.current.axis = axis;
    swipeRef.current.progress = progress;
  }, [swipeTransform]);

  const resetSwipe = useCallback(() => {
    for (const animation of swipeAnimationsRef.current) animation.cancel();
    swipeAnimationsRef.current = [];
    swipeRef.current.settling = false;
    touchStartRef.current = null;
    swipeAxisRef.current = null;
    paintSwipe("next", 0);
    for (const panel of [prevPanelRef.current, nextPanelRef.current]) {
      if (!panel) continue;
      panel.style.top = "0px";
      panel.style.height = "";
      panel.style.minHeight = "";
    }
  }, [paintSwipe]);

  // Once the incoming record is committed, reset the panels before paint.
  useLayoutEffect(() => {
    resetSwipe();
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [eventIndex, resetSwipe]);
  useEffect(() => resetSwipe, [resetSwipe]);

  const settleSwipe = useCallback((commit: boolean) => {
    const { dir, axis, progress, settling } = swipeRef.current;
    if (settling) return;
    const preview = dir === "next" ? nextPanelRef.current : prevPanelRef.current;
    const current = currentPanelRef.current;
    if (!preview || !current || progress === 0) {
      resetSwipe();
      return;
    }
    swipeRef.current.settling = true;
    const target = commit ? 1 : 0;
    const finish = () => {
      for (const animation of swipeAnimationsRef.current) animation.cancel();
      swipeAnimationsRef.current = [];
      paintSwipe(dir, target, axis);
      if (commit) {
        // The finger already performed the slide. Do not replay it on commit.
        goTo(eventIndex + (dir === "next" ? 1 : -1), dir, false);
      } else {
        resetSwipe();
      }
    };
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      finish();
      return;
    }
    const options: KeyframeAnimationOptions = {
      duration: Math.max(80, 180 * Math.abs(target - progress)),
      easing: "cubic-bezier(0.16, 1, 0.3, 1)",
      fill: "forwards",
    };
    const offset = dir === "next" ? -target : target;
    const slide = current.animate([
      { transform: current.style.transform },
      { transform: swipeTransform(offset, axis) },
    ], options);
    swipeAnimationsRef.current = [slide, preview.animate([
      { transform: preview.style.transform },
      { transform: swipeTransform((dir === "next" ? 1 : -1) + offset, axis) },
    ], options)];
    slide.onfinish = finish;
  }, [eventIndex, goTo, paintSwipe, resetSwipe, swipeTransform]);

  const handleTouchCancel = useCallback(() => {
    touchStartRef.current = null;
    swipeAxisRef.current = null;
    settleSwipe(false);
  }, [settleSwipe]);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!playlistStarted || swipeRef.current.settling || window.matchMedia(DESKTOP_SLEEVE_QUERY).matches) return;
    if (e.touches.length !== 1) {
      handleTouchCancel();
      return;
    }
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: e.timeStamp,
      fromNextButton: e.target instanceof Node && !!nextButtonRef.current?.contains(e.target),
    };
    swipeAxisRef.current = null;
  }, [playlistStarted, handleTouchCancel]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!touchStartRef.current || swipeRef.current.settling) return;
    if (e.touches.length !== 1) {
      handleTouchCancel();
      return;
    }
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;
    // Only the Next Event button accepts upward navigation. Vertical gestures
    // in the artist area stay native for the entire gesture.
    if (swipeAxisRef.current === null && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      swipeAxisRef.current = Math.abs(dx) > Math.abs(dy)
        ? "x"
        : touchStartRef.current.fromNextButton && dy < 0 && !isAtEnd ? "y" : "scroll";
      if (swipeAxisRef.current !== "scroll") {
        // New records enter at the top, even when leaving a scrolled record.
        const top = `${scrollRef.current?.scrollTop ?? 0}px`;
        for (const panel of [prevPanelRef.current, nextPanelRef.current]) {
          if (!panel) continue;
          panel.style.top = top;
          if (swipeAxisRef.current === "y") {
            // Clip tall bills to one viewport so the previous record stays offscreen.
            panel.style.height = panel.style.minHeight = `${scrollRef.current?.clientHeight ?? 0}px`;
          }
        }
      }
    }
    const axis = swipeAxisRef.current;
    if ((axis === "x" || axis === "y") && mobilePanelRef.current) {
      if (!e.cancelable) {
        handleTouchCancel();
        return;
      }
      e.preventDefault();
      const distance = axis === "x" ? dx : Math.min(dy, 0);
      const canGo = distance < 0 ? !isAtEnd : !isAtStart;
      const size = axis === "x" ? mobilePanelRef.current.clientWidth : scrollRef.current?.clientHeight ?? 0;
      paintSwipe(distance < 0 ? "next" : "prev", canGo && size > 0 ? Math.min(Math.abs(distance) / size, 1) : 0, axis);
    }
  }, [isAtStart, isAtEnd, paintSwipe, handleTouchCancel]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    const start = touchStartRef.current;
    if (!start) return;
    touchStartRef.current = null;
    const axis = swipeAxisRef.current;
    swipeAxisRef.current = null;
    if (axis !== "x" && axis !== "y") return;
    const distance = axis === "x" ? e.changedTouches[0].clientX - start.x : Math.min(e.changedTouches[0].clientY - start.y, 0);
    const canGo = distance < 0 ? !isAtEnd : !isAtStart;
    if (mobilePanelRef.current) {
      const size = axis === "x" ? mobilePanelRef.current.clientWidth : scrollRef.current?.clientHeight ?? 0;
      paintSwipe(distance < 0 ? "next" : "prev", canGo && size > 0 ? Math.min(Math.abs(distance) / size, 1) : 0, axis);
    }
    const flick = axis === "y" && Math.abs(distance) > 24 && Math.abs(distance) / Math.max(1, e.timeStamp - start.time) > 0.5;
    const commit = (Math.abs(distance) > 60 || flick) && canGo;
    settleSwipe(commit);
  }, [isAtStart, isAtEnd, paintSwipe, settleSwipe]);

  useEffect(() => {
    const surface = gestureSurfaceRef.current;
    if (!surface) return;
    surface.addEventListener("touchstart", handleTouchStart, { passive: true });
    // React's touch listeners are passive; a native listener lets vertical
    // navigation from the button prevent scrolling while keeping detail scrolling.
    surface.addEventListener("touchmove", handleTouchMove, { passive: false });
    surface.addEventListener("touchend", handleTouchEnd, { passive: true });
    surface.addEventListener("touchcancel", handleTouchCancel, { passive: true });
    return () => {
      surface.removeEventListener("touchstart", handleTouchStart);
      surface.removeEventListener("touchmove", handleTouchMove);
      surface.removeEventListener("touchend", handleTouchEnd);
      surface.removeEventListener("touchcancel", handleTouchCancel);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel]);

  // Scroll fade: hide when scrolled to bottom. Each record starts at the top.
  const [showScrollFade, setShowScrollFade] = useState(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      setShowScrollFade(el.scrollHeight - el.scrollTop - el.clientHeight > 20);
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, [eventIndex]);

  // Lock body scroll + signal player bar
  useEffect(() => {
    document.body.style.overflow = "hidden";
    setJukeboxOpen(true);
    return () => {
      document.body.style.overflow = "";
      setJukeboxOpen(false);
    };
  }, [setJukeboxOpen]);

  if (!currentEvent || !featuredArtist) return null;

  // Apply local overrides from admin edits
  const override = artistOverrides.get(featuredArtist.attractionId);
  const artistImageUrl = override?.imageUrl ?? featuredArtist.imageUrl;
  const artistName = override?.name ?? featuredArtist.name;
  const artistGenres = override?.genres ?? featuredArtist.genres ?? [];
  const sleeve: SleeveData = { artist: featuredArtist, event: currentEvent.event, imageUrl: artistImageUrl, name: artistName, genres: artistGenres };
  const sleeveProps = { data: sleeve, navSeq: nav.seq, dir: nav.dir, animate: nav.animate, citySlug, directoryTags, isAdmin, override, onEdit: setEditingArtist, playlistHref, showTickets: playlistStarted && !currentEvent.isPast };
  const adjacentSleeve = (offset: number): SleeveData | null => {
    const entry = playlist[eventIndex + offset];
    const artist = entry?.artists[0];
    if (!artist) return null;
    const override = artistOverrides.get(artist.attractionId);
    return { artist, event: entry.event, imageUrl: override?.imageUrl ?? artist.imageUrl, name: override?.name ?? artist.name, genres: override?.genres ?? artist.genres ?? [] };
  };
  const previousSleeve = adjacentSleeve(-1);
  const nextSleeve = adjacentSleeve(1);

  const renderReadout = (entry: JukeboxEvent, featured: JukeboxArtist, preview = false) => (
    <>
      {/* Event details */}
      <div className="border-b border-neutral-800 px-5 py-4">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <Link
              href={`/${citySlug}/event/${entry.event.slug}`}
              rel={eventLinkRel(entry.event)} prefetch={false}
              onClick={handleClose}
              className="block font-mono text-lg font-bold uppercase tracking-tight text-white hover:text-acid transition-colors line-clamp-2"
            >
              {entry.event.name}
            </Link>
            {entry.subtitle && (
              <p className="mt-0.5 font-mono text-xs text-acid/70 line-clamp-1">
                {entry.subtitle}
              </p>
            )}
            {entry.isPast && <p role="note" className="mt-2 font-mono text-xs text-amber-300">{t("eventPassed")}</p>}
          </div>
          <SubscribeButton
            type="artist"
            targetId={entry.artists[0]?.attractionId ?? 0}
            targetName={entry.artists[0]?.name ?? entry.event.name}
            targetImage={null}
            size="sm"
            event={entry.event}
          />
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2.5">
            <Calendar className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
            <span className="font-mono text-xs text-neutral-300">
              {formatDate(entry.event.local_date, locale, tEvents("dateTba"))}
              {entry.event.local_time && (
                <span className="text-neutral-500"> · {formatTime(entry.event.local_time, locale)}</span>
              )}
            </span>
          </div>
          {entry.event.venue.name && (
          <div className="flex items-center gap-2.5">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
            {entry.event.venue.slug ? (
              <Link
                href={`/${citySlug}/${entry.event.venue.slug}`}
                onClick={handleClose}
                className="font-mono text-xs text-neutral-300 hover:text-acid transition-colors"
              >
                {entry.event.venue.name}
              </Link>
            ) : (
              <span className="font-mono text-xs text-neutral-300">
                {entry.event.venue.name}
              </span>
            )}
          </div>
          )}
          <div className="flex items-center gap-2.5">
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center font-mono text-[10px] text-neutral-500">$</span>
            <span className="font-mono text-xs text-neutral-300">
              {formatPrice(
                entry.event.price_min,
                entry.event.price_max,
                entry.event.price_currency,
                locale,
                tEvents("priceTba")
              )}
            </span>
          </div>
        </div>

        {/* GET TICKETS button */}
        {playlistStarted && !entry.isPast && entry.event.url && (
          <a
            href={entry.event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-acid py-3 font-mono text-sm font-bold tracking-wider text-black transition-all hover:bg-acid/90 hover:shadow-[0_0_30px_rgba(209,248,5,0.3)]"
          >
            {tEvents("getTickets")}
            <ExternalLink className="h-4 w-4" />
          </a>
        )}

        {/* Add to schedule — festival jukebox only */}
        {festivalId != null && (
          <ScheduleToggle
            festivalId={festivalId}
            eventId={entry.event.id}
            attractionId={featured.attractionId}
            attractionName={featured.name}
          />
        )}
      </div>

      {/* Artists on this event */}
      <div className="px-5 py-4">
        <p className="mb-3 font-mono text-[10px] font-bold tracking-wider text-neutral-500">
          {t("artists")}
        </p>
        <div className="space-y-1">
          {entry.artists.map((artist) => {
            const rowOverride = artistOverrides.get(artist.attractionId);
            return (
              <ArtistRow
                key={artist.attractionId}
                artist={artist}
                isPlaying={activeArtistId === artist.spotifyId}
                onPlay={() => playArtist(artist.spotifyId, artist.name)}
                event={entry.event}
                override={rowOverride}
                isAdmin={!preview && isAdmin}
                onEdit={setEditingArtist}
              />
            );
          })}
        </div>
      </div>
    </>
  );

  return (
    <div
      data-jukebox-modal=""
      // Desktop bottom padding = player bar (196px) + 12px gap + nav row (44px) + 16px gap,
      // so the card centers in the space above the fixed prev/next row.
      className={`fixed inset-0 z-[100] flex items-start min-[950px]:items-center justify-center min-[950px]:p-4 min-[950px]:pb-[268px] ${standalone ? "bg-dark" : "bg-black/80 backdrop-blur-sm"}`}
      onClick={standalone ? undefined : handleClose}
    >
      {/* Modal + nav wrapper */}
      <div ref={gestureSurfaceRef} className="relative flex h-full w-full touch-pan-y touch-pinch-zoom flex-col items-center min-[950px]:h-auto min-[950px]:max-w-[900px]" onClick={(e) => e.stopPropagation()}>
        {/* Modal card */}
        <div className="relative flex w-full min-w-0 h-full min-[950px]:h-auto min-[950px]:max-h-[calc(100vh-300px)] flex-col min-[950px]:border min-[950px]:border-neutral-800 bg-surface min-[950px]:flex-row">

        {/* Corner markers — desktop only */}
        <div className="absolute -top-1 -left-1 h-3 w-3 border-t border-l border-acid hidden min-[950px]:block" />
        <div className="absolute -top-1 -right-1 h-3 w-3 border-t border-r border-acid hidden min-[950px]:block" />
        <div className="absolute -bottom-1 -left-1 h-3 w-3 border-b border-l border-acid hidden min-[950px]:block" />
        <div className="absolute -bottom-1 -right-1 h-3 w-3 border-b border-r border-acid hidden min-[950px]:block" />

        {/* Desktop: fixed left image column */}
        <div className="relative hidden min-[950px]:block w-[400px] shrink-0 overflow-hidden">
          <Sleeve {...sleeveProps} sizes={SLEEVE_SIZES.desktop} />
        </div>

        {/* Right column (desktop) / full column (mobile) */}
        <div className="relative flex min-h-0 flex-1 flex-col min-[950px]:border-l min-[950px]:border-neutral-800">
          {/* Mobile close stays over the photo as the content scrolls. */}
          <button
            type="button"
            onClick={handleClose}
            aria-label={tCommon("close")}
            className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white transition-colors hover:bg-black/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acid min-[950px]:hidden"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>

          {/* Desktop header */}
          <div className="hidden items-center justify-between border-b border-neutral-800 px-5 py-3 min-[950px]:flex">
            <div>
              <h2 className="font-mono text-sm font-bold tracking-wider text-[#1DB954]">
                {titleOverride ?? t("title")}: {genreName.toUpperCase()}
              </h2>
              {singleGenre && (
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase text-neutral-500">
                    {t("correctGenre")}
                  </span>
                  <button
                    onClick={() => {
                      if (currentGenreVote || !singleGenre || !featuredArtist) return;
                      const key = `${featuredArtist.attractionId}:${singleGenre}`;
                      genreVotedRef.current.set(key, "up");
                      setCurrentGenreVote("up");
                      submitGenreVote(featuredArtist.attractionId, singleGenre, "up");
                    }}
                    disabled={currentGenreVote !== null}
                    className={`rounded-full p-1 transition-colors ${
                      currentGenreVote === "up"
                        ? "text-green-400"
                        : currentGenreVote !== null
                          ? "text-neutral-600"
                          : "text-neutral-400 hover:bg-neutral-800 hover:text-green-400"
                    }`}
                    title="Correct genre"
                  >
                    <ThumbsUp className="h-3 w-3" />
                  </button>
                  <button
                    onClick={() => {
                      if (currentGenreVote || !singleGenre || !featuredArtist) return;
                      const key = `${featuredArtist.attractionId}:${singleGenre}`;
                      genreVotedRef.current.set(key, "down");
                      setCurrentGenreVote("down");
                      submitGenreVote(featuredArtist.attractionId, singleGenre, "down");
                    }}
                    disabled={currentGenreVote !== null}
                    className={`rounded-full p-1 transition-colors ${
                      currentGenreVote === "down"
                        ? "text-red-400"
                        : currentGenreVote !== null
                          ? "text-neutral-600"
                          : "text-neutral-400 hover:bg-neutral-800 hover:text-red-400"
                    }`}
                    title="Wrong genre"
                  >
                    <ThumbsDown className="h-3 w-3" />
                  </button>
                  {featuredArtist && (
                    <ReportButton
                      entityType="jukebox"
                      entityId={featuredArtist.attractionId}
                      entityName={featuredArtist.name}
                    />
                  )}
                </div>
              )}
              <p className="mt-0.5 font-mono text-[10px] text-neutral-500">
                {t("eventOf", { current: eventIndex + 1, total: playlist.length })}
              </p>
              {playlistError && <p role="status" className="mt-2 text-xs text-neutral-300">
                {t("loadEventsError")} {" "}
                <button type="button" onClick={onRetryPlaylist} className="inline-flex min-h-11 items-center text-acid underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-acid">{t("retry")}</button>
              </p>}
            </div>
            <button
              onClick={handleClose}
              aria-label={tCommon("close")}
              className="shrink-0 text-neutral-500 hover:text-white transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Clip the readout's horizontal slide so it cannot flash a scrollbar. */}
          <div className="relative flex-1 min-h-0">
          <div ref={scrollRef} className="h-full touch-pan-y touch-pinch-zoom overflow-x-hidden overflow-y-auto custom-scrollbar">
            {/* Mobile records share a moving panel, including their readout. */}
            <div ref={mobilePanelRef} className="relative min-h-full touch-pan-y touch-pinch-zoom overflow-hidden">
              <div ref={currentPanelRef} className="relative bg-surface" style={{ transform: "translate3d(0, 0, 0)" }}>
                <div className="relative aspect-square w-full overflow-hidden border-b border-neutral-800 min-[950px]:hidden">
                  <Sleeve {...sleeveProps} sizes={SLEEVE_SIZES.mobile} showEventInfo onNavigate={handleClose} />
                </div>
                <div
                  key={eventIndex}
                  className={nav.dir && nav.animate ? "jukebox-readout-enter" : undefined}
                  data-dir={nav.dir ?? undefined}
                >
                  {renderReadout(currentEvent, featuredArtist)}
                </div>
              </div>
              {previousSleeve && (
                <div ref={prevPanelRef} className="pointer-events-none absolute inset-x-0 top-0 min-h-full overflow-hidden bg-surface min-[950px]:hidden" style={{ transform: "translate3d(-100%, 0, 0)" }} inert aria-hidden>
                  <div className="relative aspect-square w-full overflow-hidden border-b border-neutral-800">
                    <SleeveFace data={previousSleeve} sizes={SLEEVE_SIZES.mobile} citySlug={citySlug} directoryTags={directoryTags} showEventInfo showTickets={playlistStarted && !playlist[eventIndex - 1].isPast} />
                  </div>
                  {renderReadout(playlist[eventIndex - 1], previousSleeve.artist, true)}
                </div>
              )}
              {nextSleeve && (
                <div ref={nextPanelRef} className="pointer-events-none absolute inset-x-0 top-0 min-h-full overflow-hidden bg-surface min-[950px]:hidden" style={{ transform: "translate3d(100%, 0, 0)" }} inert aria-hidden>
                  <div className="relative aspect-square w-full overflow-hidden border-b border-neutral-800">
                    <SleeveFace data={nextSleeve} sizes={SLEEVE_SIZES.mobile} citySlug={citySlug} directoryTags={directoryTags} showEventInfo showTickets={playlistStarted && !playlist[eventIndex + 1].isPast} />
                  </div>
                  {renderReadout(playlist[eventIndex + 1], nextSleeve.artist, true)}
                </div>
              )}
            </div>
          </div>
          {/* Scroll fade indicator (mobile) */}
          {showScrollFade && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-surface to-transparent min-[950px]:hidden" />
          )}
          </div>

          {/* Footer: nav on mobile only — desktop nav is fixed above the player bar. */}
          <div className={`flex items-center gap-3 border-t border-neutral-800 px-4 py-3 min-[950px]:hidden ${playlistStarted ? "" : "fixed inset-x-0 bottom-0 z-20 bg-surface pb-[calc(env(safe-area-inset-bottom)+0.75rem)]"}`}>
            {!playlistStarted ? (
              <button type="button" onClick={startPlayer}
                className="jukebox-start flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-acid px-6 py-3 font-mono text-sm font-bold tracking-wider text-black transition-colors hover:bg-acid/90 active:bg-acid/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acid">
                <PlayIcon className="h-4 w-4" aria-hidden="true" />
                {t("startPlayer")}
              </button>
            ) : <>
            <button
              onClick={goPrev}
              disabled={isAtStart}
              className="flex shrink-0 items-center gap-2 rounded-lg border border-neutral-700 bg-dark/90 py-3 pl-4 pr-5 font-mono text-sm font-bold tracking-wider text-neutral-400 transition-all enabled:hover:border-neutral-500 enabled:hover:text-white enabled:active:border-neutral-500 enabled:active:text-white disabled:opacity-20"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("prev")}
            </button>
            <button
              ref={nextButtonRef}
              onClick={goNext}
              disabled={isAtEnd}
              className="flex min-w-0 flex-1 touch-none select-none items-center justify-center gap-2 rounded-lg bg-acid px-6 py-3 font-mono text-sm font-bold tracking-wider text-black transition-all enabled:hover:bg-acid/90 enabled:active:bg-acid/90 disabled:opacity-20"
            >
              {t(isAtEnd && playlistPending ? "loadingEvents" : "nextEvent")}
              <ChevronRight className="h-4 w-4" />
            </button>
            </>}
          </div>
          {/* Reserve only the start action until the player enters. */}
          <div className={`shrink-0 min-[950px]:hidden ${playlistStarted ? "h-[200px]" : "h-[calc(4.5rem+env(safe-area-inset-bottom))]"}`} />
        </div>
        </div>
      </div>

      {/* Desktop nav — fixed just above the Spotify player bar (196px tall), aligned to the card's width */}
      <div className="fixed inset-x-0 bottom-[208px] hidden min-[950px]:block">
        <div
          className="mx-auto flex w-full max-w-[900px] items-center justify-between gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          {!playlistStarted ? (
            <button type="button" onClick={startPlayer}
              className="jukebox-start mx-auto flex min-h-11 min-w-[260px] items-center justify-center gap-2 rounded-lg bg-acid px-8 py-3 font-mono text-sm font-bold tracking-wider text-black transition-colors hover:bg-acid/90 active:bg-acid/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acid">
              <PlayIcon className="h-4 w-4" aria-hidden="true" />
              {t("startPlayer")}
            </button>
          ) : <>
          <button
            onClick={goPrev}
            disabled={isAtStart}
            className="flex shrink-0 items-center gap-2 rounded-lg border border-neutral-700 bg-dark/90 py-3 pl-4 pr-5 font-mono text-sm font-bold tracking-wider text-neutral-400 backdrop-blur-sm transition-all enabled:hover:border-neutral-500 enabled:hover:text-white disabled:opacity-20"
          >
            <ChevronLeft className="h-4 w-4" />
            {t("prevEvent")}
          </button>
          <button
            onClick={goNext}
            disabled={isAtEnd}
            className="flex min-w-[260px] items-center justify-center gap-2 rounded-lg bg-acid px-8 py-3 font-mono text-sm font-bold tracking-wider text-black transition-all enabled:hover:bg-acid/90 enabled:hover:shadow-[0_0_30px_rgba(209,248,5,0.3)] disabled:opacity-20"
          >
            {t(isAtEnd && playlistPending ? "loadingEvents" : "nextEvent")}
            <ChevronRight className="h-4 w-4" />
          </button>
          </>}
        </div>
      </div>

      {/* Admin: artist edit modal */}
      {editingArtist && (
        <ArtistEditModal
          artist={editingArtist}
          onClose={() => setEditingArtist(null)}
          onSave={(updated) => {
            setArtistOverrides((prev) => {
              const next = new Map(prev);
              next.set(editingArtist.id, {
                imageUrl: updated.image_url,
                genres: updated.genres,
                name: updated.name,
              });
              return next;
            });
          }}
        />
      )}
    </div>
  );
}

function ScheduleToggle({
  festivalId,
  eventId,
  attractionId,
  attractionName,
}: {
  festivalId: number;
  eventId: number;
  attractionId: number;
  attractionName: string;
}) {
  const { savedSetKeys, toggleScheduleItem } = useScheduleBuilder();
  const isSaved = savedSetKeys.has(`${eventId}-${attractionId}`);

  return (
    <button
      onClick={() => toggleScheduleItem(festivalId, eventId, attractionId, attractionName)}
      className={`mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border py-3 font-mono text-sm font-bold tracking-wider transition-all ${
        isSaved
          ? "border-acid/30 bg-acid/10 text-acid hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400 group"
          : "border-neutral-700 text-neutral-400 hover:border-acid/50 hover:text-acid"
      }`}
    >
      {isSaved ? (
        <>
          <Check className="h-4 w-4 group-hover:hidden" />
          <X className="h-4 w-4 hidden group-hover:block" />
          <span className="group-hover:hidden">IN MY SCHEDULE</span>
          <span className="hidden group-hover:inline">REMOVE</span>
        </>
      ) : (
        <>
          <Plus className="h-4 w-4" />
          ADD TO SCHEDULE
        </>
      )}
    </button>
  );
}

interface SleeveFaceProps {
  data: SleeveData;
  sizes: string;
  citySlug: string;
  directoryTags: boolean;
  showEventInfo?: boolean;
  showTickets?: boolean;
  onNavigate?: () => void;
  isAdmin?: boolean;
  override?: { imageUrl: string | null; genres: string[]; name: string };
  onEdit?: (input: ArtistEditInput) => void;
  playlistHref?: string;
}

/**
 * The artist image column. When the featured artist changes, the previous face
 * stays mounted underneath while the new one animates in on top: a next/prev
 * move slides it in while pushing the previous face out, and any
 * other change (playing another act on the same bill) crossfades.
 */
function Sleeve({ navSeq, dir, animate, ...face }: SleeveFaceProps & { navSeq: number; dir: NavDir | null; animate: boolean }) {
  const key = face.data.artist.attractionId;
  const [outgoing, setOutgoing] = useState<{ data: SleeveData; dir: NavDir | null } | null>(null);
  const [last, setLast] = useState({ key, navSeq, data: face.data });

  // Adjust the transition with the new record, before React commits its face.
  if (last.data !== face.data || last.navSeq !== navSeq) {
    if (last.key !== key) {
      setOutgoing(last.navSeq !== navSeq && !animate ? null : { data: last.data, dir: last.navSeq !== navSeq ? dir : null });
    }
    setLast({ key, navSeq, data: face.data });
  }

  // The mobile and desktop sleeves both mount; the hidden one never fires
  // animationend, so the outgoing face is dropped on a timer as well.
  useEffect(() => {
    if (!outgoing) return;
    const timer = setTimeout(() => setOutgoing(null), 600);
    return () => clearTimeout(timer);
  }, [outgoing]);

  return (
    <>
      {outgoing && (
        <div key={`out-${outgoing.data.artist.attractionId}`} className="absolute inset-0 jukebox-sleeve-exit" data-dir={outgoing.dir ?? undefined} inert aria-hidden>
          <SleeveFace {...face} data={outgoing.data} isAdmin={false} />
        </div>
      )}
      <div
        key={key}
        className={outgoing ? "absolute inset-0 jukebox-sleeve-enter" : "absolute inset-0"}
        data-dir={outgoing?.dir ?? undefined}
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget) setOutgoing(null);
        }}
      >
        <SleeveFace {...face} />
      </div>
    </>
  );
}

function SleeveFace({ data, sizes, citySlug, directoryTags, showEventInfo, showTickets, onNavigate, isAdmin, override, onEdit, playlistHref }: SleeveFaceProps) {
  const { artist, event, imageUrl, name, genres } = data;
  const shareButton = playlistHref ? <JukeboxShareButton playlistHref={playlistHref} eventId={event.id} artistId={artist.attractionId} artistName={name} eventName={event.name} /> : null;

  if (!imageUrl) {
    return (
      <div className="relative flex h-full w-full items-center justify-center bg-neutral-900">
        <div className="text-center">
          <span className="font-mono text-6xl text-neutral-700">
            {name.charAt(0).toUpperCase()}
          </span>
          <p className="mt-3 font-mono text-lg font-bold uppercase tracking-tight text-neutral-400">
            {name}
          </p>
          {isAdmin && onEdit && <div className="mt-2"><AdminEditButton artist={artist} override={override} onEdit={onEdit} /></div>}
        </div>
        {showEventInfo && (
          <div className={`absolute inset-x-5 bottom-4 ${playlistHref ? "pr-12" : ""}`}>
            <SleeveEventInfo event={event} citySlug={citySlug} onNavigate={onNavigate} showTickets={showTickets} />
          </div>
        )}
        {shareButton}
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <Image
        src={imageUrl}
        alt={name}
        fill
        className="object-cover"
        sizes={sizes}
      />
      <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t to-transparent px-5 pb-4 ${playlistHref ? "pr-16" : ""} ${showEventInfo ? "from-black/95 via-black/60 pt-16" : "from-black/80 pt-12"}`}>
        <div className="flex items-center gap-2">
          <Link
            href={`/artist/${artist.slug}`}
            rel={jukeboxArtistLinkRel(artist)} prefetch={false}
            className="font-mono text-2xl font-bold uppercase tracking-tight text-white hover:text-acid transition-colors drop-shadow-lg line-clamp-2"
          >
            {name}
          </Link>
          {isAdmin && onEdit && <AdminEditButton artist={artist} override={override} onEdit={onEdit} />}
        </div>
        {!showEventInfo && genres.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {[...new Set(genres)].slice(0, 3).map((g) => (
              directoryTags ? <EventGenreChip key={g} genre={g} citySlug={citySlug} pill /> : <span key={g} className="rounded-full border border-white/20 bg-black/30 px-2 py-0.5 font-mono text-[9px] text-white/70 backdrop-blur-sm">
                {g.toUpperCase()}
              </span>
            ))}
          </div>
        )}
        {!showEventInfo && artist.externalLinks && Object.keys(artist.externalLinks).length > 0 && (
          <div className="mt-2">
            <ArtistExternalLinks links={artist.externalLinks} size="sm" />
          </div>
        )}
        {showEventInfo && (
          <SleeveEventInfo event={event} citySlug={citySlug} onNavigate={onNavigate} showTickets={showTickets} />
        )}
      </div>
      {shareButton}
    </div>
  );
}

function SleeveEventInfo({ event, citySlug, onNavigate, showTickets = true }: { event: Event; citySlug: string; onNavigate?: () => void; showTickets?: boolean }) {
  const tEvents = useTranslations("Events");
  const locale = useLocale();
  const ticketUrl = event.url || event.venue.url;
  const price = formatPriceCompact(event.price_min, event.price_max, event.price_currency, locale, tEvents("priceTba"));

  return (
    <div className="mt-2 flex min-w-0 flex-col items-start text-left">
      {event.venue.name && (
        <div className="flex max-w-full items-start gap-1.5">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-acid/70" aria-hidden="true" />
          {event.venue.slug ? (
            <Link
              href={`/${citySlug}/${event.venue.slug}`}
              onClick={onNavigate}
              className="min-w-0 text-sm leading-5 text-white transition-colors hover:text-acid line-clamp-2"
            >
              {event.venue.name}
            </Link>
          ) : (
            <span className="min-w-0 text-sm leading-5 text-white line-clamp-2">{event.venue.name}</span>
          )}
        </div>
      )}
      <div className={`mt-1 flex max-w-full items-center gap-3 ${event.venue.name ? "pl-5" : ""}`}>
        <Link
          href={`/${citySlug}/event/${event.slug}`}
          rel={eventLinkRel(event)}
          prefetch={false}
          onClick={onNavigate}
          className="min-w-0 font-mono text-xs uppercase leading-4 text-acid transition-colors hover:text-white"
        >
          {formatDate(event.local_date, locale, tEvents("dateTba"))}
        </Link>
        {showTickets && (ticketUrl ? (
          <a
            href={ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-ticket-link=""
            data-event-id={event.id}
            className="flex shrink-0 items-center gap-1 font-mono text-xs leading-4 text-neutral-300 transition-colors hover:text-acid"
          >
            {price}
            <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
          </a>
        ) : (
          <span className="font-mono text-xs leading-4 text-neutral-300">{price}</span>
        ))}
      </div>
    </div>
  );
}

function AdminEditButton({
  artist,
  override,
  onEdit,
}: {
  artist: JukeboxArtist;
  override?: { imageUrl: string | null; genres: string[]; name: string };
  onEdit: (input: ArtistEditInput) => void;
}) {
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onEdit({
          id: artist.attractionId,
          name: override?.name ?? artist.name,
          slug: artist.slug,
          genres: override?.genres ?? artist.genres ?? [],
          spotify_id: artist.spotifyId,
          image_url: override?.imageUrl ?? artist.imageUrl,
        });
      }}
      className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-black/50 text-neutral-400 backdrop-blur-sm transition-colors hover:text-acid hover:bg-black/70"
      title="Edit artist"
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
        <path d="m15 5 4 4" />
      </svg>
    </button>
  );
}

function ArtistRow({
  artist,
  isPlaying,
  onPlay,
  event,
  override,
  isAdmin,
  onEdit,
}: {
  artist: JukeboxArtist;
  isPlaying: boolean;
  onPlay: () => void;
  event: Event;
  override?: { imageUrl: string | null; genres: string[]; name: string };
  isAdmin?: boolean;
  onEdit?: (input: ArtistEditInput) => void;
}) {
  const imageUrl = override?.imageUrl ?? artist.imageUrl;
  const name = override?.name ?? artist.name;

  return (
    <div
      className={`flex items-center gap-2.5 rounded px-2 py-2 transition-colors ${
        isPlaying ? "bg-[#1DB954]/10" : "hover:bg-neutral-800/50"
      }`}
    >
      <Link
        href={`/artist/${artist.slug}`}
        rel={jukeboxArtistLinkRel(artist)} prefetch={false}
        className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full border border-neutral-700"
      >
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={name}
            fill
            className="object-cover"
            sizes="40px"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-800 font-mono text-xs text-neutral-400">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <Link
          href={`/artist/${artist.slug}`}
          rel={jukeboxArtistLinkRel(artist)} prefetch={false}
          className="block truncate font-mono text-sm text-neutral-200 transition-colors hover:text-acid"
        >
          {name}
        </Link>
        {artist.externalLinks && Object.keys(artist.externalLinks).length > 0 && (
          <div className="mt-0.5">
            <ArtistExternalLinks links={artist.externalLinks} size="sm" />
          </div>
        )}
      </div>
      {isAdmin && onEdit && (
        <AdminEditButton artist={artist} override={override} onEdit={onEdit} />
      )}
      <SubscribeButton
        type="artist"
        targetId={artist.attractionId}
        targetName={name}
        targetImage={imageUrl}
        size="sm"
        event={event}
      />
      <button
        onClick={onPlay}
        title={`Preview ${name}`}
        className={`flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all ${
          isPlaying
            ? "bg-[#1DB954]/20 text-[#1DB954] shadow-[0_0_8px_rgba(29,185,84,0.3)]"
            : "text-neutral-400 hover:text-[#1DB954] hover:bg-neutral-700"
        }`}
      >
        <PlayIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
